import { useState, useEffect, useRef, useCallback } from "react";
import { useLanguage } from "@/lib/translation/useLanguage";
import { SUPPORTED_LANGUAGES } from "@/lib/translation/language";

interface UseSpeechRecognitionReturn {
  isListening: boolean;
  isSupported: boolean;
  toggleListening: () => void;
}

export const useSpeechRecognition = (
  onTranscript: (text: string) => void,
): UseSpeechRecognitionReturn => {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const listeningRef = useRef(false);
  const transcriptRef = useRef(onTranscript);
  useEffect(() => {
    transcriptRef.current = onTranscript;
  }, [onTranscript]);
  const { language } = useLanguage();

  const isSupported = !!(
    (window as Window).SpeechRecognition ||
    (window as Window).webkitSpeechRecognition
  );

  useEffect(() => {
    const SpeechRecognition =
      (window as Window).SpeechRecognition ||
      (window as Window).webkitSpeechRecognition;

    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      let transcript = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript.trim()) {
        if (recognitionRef.current === recognition && listeningRef.current)
          transcriptRef.current(transcript + " ");
      }
    };

    recognition.onerror = (event: Event) => {
      const errorEvent = event as SpeechRecognitionEvent & { error?: string };
      if (errorEvent.error !== "no-speech") {
        listeningRef.current = false;
        setIsListening(false);
      }
    };

    recognition.onend = () => {
      if (recognitionRef.current !== recognition) return;
      if (listeningRef.current) {
        try {
          recognition.start();
        } catch {
          listeningRef.current = false;
          setIsListening(false);
        }
      } else {
        setIsListening(false);
      }
    };

    recognitionRef.current = recognition;
    listeningRef.current = false;

    return () => {
      recognitionRef.current = null;
      listeningRef.current = false;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.stop();
    };
  }, []);

  useEffect(() => {
    if (recognitionRef.current) {
      recognitionRef.current.lang =
        SUPPORTED_LANGUAGES.find((item) => item.code === language)?.locale ??
        "en-US";
    }
  }, [language]);

  const toggleListening = useCallback(() => {
    if (!recognitionRef.current) return;

    if (listeningRef.current) {
      listeningRef.current = false;
      recognitionRef.current.stop();
      setIsListening(false);
    } else {
      try {
        listeningRef.current = true;
        recognitionRef.current.start();
        setIsListening(true);
      } catch {
        listeningRef.current = false;
        setIsListening(false);
      }
    }
  }, []);

  return { isListening, isSupported, toggleListening };
};
